import { useCallback, useEffect, useState, type FormEvent } from "react";
import Field from "../../components/Field";
import Modal from "../../components/Modal";
import FormMessage from "../../components/FormMessage";
import Avatar from "../../components/Avatar";
import AdminBadge from "../../components/AdminBadge";
import { ApiError, apiFetch } from "../../lib/api";
import { useAuth } from "../../lib/auth";

type Member = {
  id: string;
  name: string;
  fullName: string;
  photoUrl: string | null;
  bio: string | null;
  isAdmin: boolean;
};

export default function Members() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<{ members: Member[] }>("/api/members");
      setMembers(data.members);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load members.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function suspend(m: Member) {
    if (!confirm(`Suspend ${m.fullName}? They'll be signed out immediately.`)) return;
    try {
      await apiFetch(`/api/members/${m.id}/suspend`, { method: "POST" });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to suspend.");
    }
  }

  async function remove(m: Member) {
    if (!confirm(`Permanently delete ${m.fullName}? This can't be undone.`))
      return;
    try {
      await apiFetch(`/api/members/${m.id}`, { method: "DELETE" });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete.");
    }
  }

  return (
    <>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          gap: "1rem",
          flexWrap: "wrap",
          marginBottom: "2rem",
        }}
      >
        <div>
          <div className="eyebrow">Members</div>
          <h1
            className="display display--lg"
            style={{ marginTop: "0.5rem", marginBottom: 0 }}
          >
            the besties.
          </h1>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setInviting(true)}
          >
            + Invite admin
          </button>
        )}
      </header>

      {error && <FormMessage tone="error">{error}</FormMessage>}

      {!members ? (
        <p className="eyebrow">Loading…</p>
      ) : members.length === 0 ? (
        <p style={{ color: "var(--c-palm)", opacity: 0.7 }}>
          No members yet — once people are approved and active, they'll show
          up here.
        </p>
      ) : (
        <ul
          className="members-grid"
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
          }}
        >
          {members.map((m) => (
            <li
              key={m.id}
              style={{
                background: "#fff",
                border: "1px solid var(--c-rule)",
                borderRadius: "var(--r-md)",
                padding: "0.85rem 0.65rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: "0.45rem",
              }}
            >
              <Avatar name={m.name} photoUrl={m.photoUrl} size={64} />
              <div
                style={{
                  fontWeight: 600,
                  color: "var(--c-palm)",
                  fontSize: "0.92rem",
                  lineHeight: 1.25,
                  marginTop: "0.15rem",
                }}
              >
                {m.name}
              </div>
              {m.isAdmin && <AdminBadge />}
              {m.bio && (
                <p
                  className="member-bio"
                  style={{
                    fontSize: "0.78rem",
                    color: "var(--c-ink)",
                    opacity: 0.85,
                    lineHeight: 1.4,
                    marginTop: "0.2rem",
                    marginBottom: 0,
                  }}
                >
                  {m.bio}
                </p>
              )}
              {isAdmin && m.id !== user?.id && (
                <div
                  style={{
                    display: "flex",
                    gap: "0.4rem",
                    marginTop: "0.5rem",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => suspend(m)}
                    style={{
                      padding: "0.35rem 0.7rem",
                      fontFamily: "var(--f-label)",
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "var(--c-palm)",
                      background: "transparent",
                      borderRadius: "var(--r-pill)",
                    }}
                  >
                    Suspend
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(m)}
                    style={{
                      padding: "0.35rem 0.7rem",
                      fontFamily: "var(--f-label)",
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      color: "var(--c-coral)",
                      background: "transparent",
                      borderRadius: "var(--r-pill)",
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {inviting && (
        <InviteAdminModal
          onClose={() => setInviting(false)}
          onSaved={async () => {
            setInviting(false);
            await refresh();
          }}
        />
      )}
    </>
  );
}

function InviteAdminModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const res = await apiFetch<{ ok: boolean; invited: boolean; message?: string }>(
        "/api/members/admins/invite",
        {
          method: "POST",
          body: {
            email: email.trim(),
            fullName: fullName.trim(),
            preferredName: preferredName.trim() || null,
          },
        },
      );
      setSuccess(
        res.invited
          ? "Admin invite sent. They'll get an email with a link to set their password."
          : res.message ?? "Done.",
      );
      // Don't close immediately — let them see the success and either close
      // or invite another.
      setTimeout(async () => {
        await onSaved();
      }, 1200);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to invite.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Invite an admin">
      {error && <FormMessage tone="error">{error}</FormMessage>}
      {success && <FormMessage tone="success">{success}</FormMessage>}
      <form onSubmit={onSubmit} noValidate>
        <Field
          label="Email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <Field
          label="Full name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.currentTarget.value)}
        />
        <Field
          label="What friends call them"
          hint="Optional — used in the directory."
          value={preferredName}
          onChange={(e) => setPreferredName(e.currentTarget.value)}
        />
        <p
          style={{
            fontSize: "0.85rem",
            color: "var(--c-palm)",
            opacity: 0.75,
            marginTop: "-0.25rem",
            marginBottom: "1rem",
            lineHeight: 1.5,
          }}
        >
          They'll get an email with a link to set their password. Admins skip
          membership dues and can edit the current read, post in discussions,
          create events, and review applications.
        </p>
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn--ghost"
            disabled={submitting}
          >
            Close
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Send invite"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
